import { useEffect, useRef } from "react";
import * as THREE from "three";

/* Double helix — genetics (seeds) meets molecular science (pharma) */
export const DnaHelix = ({ className = "" }) => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 100);
        camera.position.set(0, 0, 6.4);
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        mount.appendChild(renderer.domElement);

        const helix = new THREE.Group();
        scene.add(helix);

        const TURNS = 3.2;
        const NODES = 120;
        const RADIUS = 1.05;
        const HEIGHT = 4.4;

        const strandA = [];
        const strandB = [];
        const ptPos = [];
        const ptCol = [];
        const blue = new THREE.Color("#8FA0FF");
        const white = new THREE.Color("#FFFFFF");
        const orange = new THREE.Color("#F16622");

        for (let i = 0; i < NODES; i++) {
            const t = (i / (NODES - 1)) * Math.PI * 2 * TURNS;
            const y = -HEIGHT / 2 + (i / (NODES - 1)) * HEIGHT;
            strandA.push(new THREE.Vector3(Math.cos(t) * RADIUS, y, Math.sin(t) * RADIUS));
            strandB.push(new THREE.Vector3(Math.cos(t + Math.PI) * RADIUS, y, Math.sin(t + Math.PI) * RADIUS));
        }

        [strandA, strandB].forEach((strand, si) => {
            strand.forEach((p, i) => {
                ptPos.push(p.x, p.y, p.z);
                const c = i % 12 === 0 ? orange : si === 0 ? blue : white;
                ptCol.push(c.r, c.g, c.b);
            });
        });

        const ptGeo = new THREE.BufferGeometry();
        ptGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(ptPos), 3));
        ptGeo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(ptCol), 3));
        helix.add(new THREE.Points(ptGeo, new THREE.PointsMaterial({ size: 0.045, vertexColors: true, transparent: true, opacity: 0.9 })));

        /* strands as thin lines */
        [strandA, strandB].forEach((strand, si) => {
            const geo = new THREE.BufferGeometry().setFromPoints(strand);
            helix.add(
                new THREE.Line(
                    geo,
                    new THREE.LineBasicMaterial({ color: si === 0 ? 0x8fa0ff : 0xffffff, transparent: true, opacity: 0.22 })
                )
            );
        });

        /* base-pair rungs */
        const rungPos = [];
        for (let i = 0; i < NODES; i += 5) {
            rungPos.push(strandA[i].x, strandA[i].y, strandA[i].z, strandB[i].x, strandB[i].y, strandB[i].z);
        }
        const rungGeo = new THREE.BufferGeometry();
        rungGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(rungPos), 3));
        helix.add(
            new THREE.LineSegments(
                rungGeo,
                new THREE.LineBasicMaterial({ color: 0xf16622, transparent: true, opacity: 0.28 })
            )
        );

        /* orbiting molecule accent */
        const orbit = new THREE.Mesh(
            new THREE.TorusGeometry(2.35, 0.004, 8, 200),
            new THREE.MeshBasicMaterial({ color: 0xf16622, transparent: true, opacity: 0.4 })
        );
        orbit.rotation.x = Math.PI / 2.4;
        scene.add(orbit);
        const sat = new THREE.Mesh(new THREE.SphereGeometry(0.05, 16, 16), new THREE.MeshBasicMaterial({ color: 0xf16622 }));
        scene.add(sat);

        let mx = 0;
        let my = 0;
        const onMouse = (e) => {
            mx = (e.clientX / window.innerWidth - 0.5) * 2;
            my = (e.clientY / window.innerHeight - 0.5) * 2;
        };
        window.addEventListener("mousemove", onMouse);

        let raf;
        const clock = new THREE.Clock();
        const animate = () => {
            const t = clock.getElapsedTime();
            helix.rotation.y = t * 0.28 + mx * 0.25;
            helix.rotation.x = Math.sin(t * 0.18) * 0.08 + my * 0.12;
            const a = t * 0.45;
            sat.position.set(Math.cos(a) * 2.35, Math.sin(a) * 2.35, 0);
            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };
        animate();

        const onResize = () => {
            camera.aspect = mount.clientWidth / mount.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(mount.clientWidth, mount.clientHeight);
        };
        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMouse);
            window.removeEventListener("resize", onResize);
            if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
            ptGeo.dispose();
            rungGeo.dispose();
            renderer.dispose();
        };
    }, []);

    return <div ref={mountRef} className={className} data-testid="dna-helix" />;
};
